/**
 * @file MeetingNotesPanel.tsx
 * @brief Notes during a live meeting: write one, or attach notes you already
 *        have. Private to the writer. Shared by the desktop widget's popup
 *        (dark) and the web recorder (page theme).
 *
 * @details
 * DES 02-10-2026 (memory: in-session-notes-all-platforms). A note written here
 * is an ordinary typed Verblizr note, saved as you type. The recorder keeps the
 * ids and, when the meeting is uploaded, links each one with the PRIVATE
 * `addendum` role, so none of them steers the shared Meeting Intel; each then
 * gets its own "From the meeting" report for its writer.
 *
 * Line timing (ADR-030, 06-10-2026): when the caller passes
 * `getElapsedSecs`, each line is stamped with the second of the recording at
 * which it first reached its current text, and every save sends those
 * seconds as `pointOffsets`, so the meeting's "Your notes" report looks at
 * what was said around each line. A line edited later takes the later second,
 * which is when its owner wrote what it now says. Callers without a clock
 * (none today) save exactly as before. Mirrors the phone's MeetingRecorder
 * `stampLines` / `offsetsFor`.
 *
 * Meeting Notes (DES 07-10-2026, the phone's cabea16, deed9d4, a6d85c3):
 * the panel is headed "Take notes" and has a title field, "Meeting title
 * (optional)", prefilled with the title given in Setup; what is typed there
 * names the meeting when Setup gave none (`titleField`). No "Attach an
 * existing note" there (`allowAttach`): points are written live, and each
 * attached note costs its own report.
 *
 * Page look (DES 07-10-2026, "feel and look bilkul waisay jo actual notes
 * likhtay howay ati hai"): on the web recorder the notepad is the Write a
 * Note page (NoteComposePage): a large borderless title, then the points as
 * numbered rows on a timeline, no boxes; Return opens the next point and
 * Backspace on an empty one removes it. The text underneath is unchanged
 * (one point per line), so saving and line timing are unchanged. The
 * desktop widget's popup has the same look, smaller (DES 07-10-2026).
 *
 * Sub-points (DES 12-10-2026, as the phone): Tab makes a point a sub-point
 * of the one above, Shift+Tab a point again; Return keeps the level and
 * Backspace on an empty sub-point first brings it back to a point. Inside
 * this file the text marks a sub-point's line with a leading tab
 * (`SUB_MARK`), so an indent alone changes the text and is saved like any
 * edit; what goes to the server is clean lines plus `pointLevels`.
 *
 * ✕ on an attached note only drops it from this meeting's list. The note itself
 * is never deleted.
 *
 * The ids live with the recorder (`meetingNoteIds`, `writingNoteId`); this
 * panel mirrors them locally so a tick shows at once, before the recorder's
 * next state arrives (the widget hears back over IPC).
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Circle,
  CircleCheck,
  LoaderCircle,
  Lock,
  Paperclip,
  PenLine,
  Search,
  X,
} from "lucide-react";
import { cn } from "@/shared/theme/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { formatDateTime, isRTLText } from "@/shared/utils/formatters";
import { useCreateTypedNote, useNote, useNotesList, useUpdateNote } from "./api";
import { notePointLevels, notePoints } from "./schemas";
import { pointLabels } from "./pointLevels";
import { capitalizeFirst } from "./capitalizeFirst";

const SAVE_DELAY_MS = 800;

type Tone = "widget" | "page";

const PALETTE: Record<
  Tone,
  {
    surface: string;
    chip: string;
    muted: string;
    field: string;
    text: string;
    line: string;
    dashed: string;
    hover: string;
  }
> = {
  // The desktop widget is always the dark pill (it floats over other apps).
  widget: {
    surface: "rounded-[24px] bg-[#232221] px-3.5 py-3 text-[#f2f2f2]",
    chip: "bg-[#3A3938] text-[#f2f2f2] hover:bg-[#3E3D3C]",
    muted: "text-[#A3A3A3]",
    field: "bg-[#2A2928]",
    text: "text-[#f2f2f2]",
    line: "border-[#2A2928]",
    dashed: "border-[#5f5e5a]",
    hover: "hover:bg-[#2A2928]",
  },
  page: {
    surface: "text-foreground",
    chip: "bg-secondary text-foreground hover:bg-muted",
    muted: "text-muted-foreground",
    field: "bg-secondary",
    text: "text-foreground",
    line: "border-border",
    dashed: "border-border",
    hover: "hover:bg-secondary",
  },
};

export type MeetingNotesPanelProps = {
  tone: Tone;
  meetingTitle: string;
  agendaNoteIds: string[];
  meetingNoteIds: string[];
  writingNoteId: string | null;
  onChange: (meetingNoteIds: string[], writingNoteId: string | null) => void;
  /** Widget popup: its ✕. Omitted on the page, where the panel stays put. */
  onClose?: () => void;
  /**
   * The recording's elapsed seconds right now (ADR-030). When given, each
   * line is stamped with the second it was typed at and saves carry
   * `pointOffsets`. Read at stamp time, so it may change every render.
   */
  getElapsedSecs?: () => number;
  /** Header text; "Your notes" by default, "Take notes" in Meeting Notes. */
  heading?: string;
  /** The "Attach an existing note" link; off in Meeting Notes. Default on. */
  allowAttach?: boolean;
  /**
   * Meeting Notes: a title field that names the meeting. `initial` is the
   * Setup title; every change is reported so the recorder can send it.
   */
  titleField?: { initial: string; onChange: (title: string) => void };
};

/** The note's non-blank lines, trimmed: the server's `splitPoints`, so the
 *  offsets line up with what it stores. */
function noteLines(value: string): string[] {
  return value
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * Sub-points (DES 12-10-2026): in this file's text a sub-point's line starts
 * with a tab. Typing never inserts one (Tab is the indent key and pasted tabs
 * become spaces), so the mark cannot be confused with what was written.
 */
const SUB_MARK = "\t";

/** Levels aligned with `noteLines(value)`: 0 / 1, the first always 0. */
function noteLevels(value: string): number[] {
  return value
    .split("\n")
    .filter((l) => l.trim())
    .map((l, i) => (i > 0 && l.startsWith(SUB_MARK) ? 1 : 0));
}

/** The panel's text for a note's points: one line each, sub-points marked. */
function pointsText(lines: string[], levels: number[] | null): string {
  return lines.map((l, i) => (levels?.[i] ? SUB_MARK + l : l)).join("\n");
}

/** `point_offsets` from the note row (passthrough field), when aligned. */
function storedOffsets(note: unknown, lines: string[]): Array<number | null> | null {
  if (!note || typeof note !== "object") return null;
  const raw = (note as { point_offsets?: unknown }).point_offsets;
  if (!Array.isArray(raw) || raw.length !== lines.length) return null;
  return raw.map((v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null));
}

function Header({
  tone,
  title,
  titleAdornment,
  right,
  onClose,
}: {
  tone: Tone;
  title: string;
  /** Beside the title: the hint icon on the web recorder. */
  titleAdornment?: ReactNode;
  right?: ReactNode;
  onClose?: () => void;
}) {
  const p = PALETTE[tone];
  return (
    <div className="flex items-center gap-2">
      <PenLine className="h-4 w-4 text-brand" />
      <span
        className={cn("font-medium", tone === "page" && "text-sm font-semibold tracking-tight")}
      >
        {title}
      </span>
      {titleAdornment}
      <span className="flex-1" />
      {right}
      {onClose ? (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={cn("inline-flex h-6 w-6 items-center justify-center rounded-full", p.chip)}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

function AttachedRow({ tone, id, onRemove }: { tone: Tone; id: string; onRemove: () => void }) {
  const p = PALETTE[tone];
  const note = useNote(id);
  // Click to read it in a menu beside the row, as the app's other ⋯ menus
  // (DES 11-10-2026: "menu open style rather than expand"). Never a new page:
  // leaving the recorder mid-meeting asks "Leave this recording?".
  const title = note.data?.title?.trim() || (note.isLoading ? "…" : "Untitled note");
  const points = note.data ? notePoints(note.data) : [];
  // Sub-points (DES 12-10-2026): lettered and indented under their point.
  const levels = note.data ? notePointLevels(note.data, points) : null;
  const labels = pointLabels(levels, points.length);
  return (
    <div className={cn("flex items-center gap-1 rounded-full py-1.5 pl-3 pr-1.5 text-xs", p.field, p.text)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left">
            {/* DES 08-10-2026: app icon, was NotebookPen */}
            <PenLine className="h-3.5 w-3.5 shrink-0 text-brand" />
            <span className="min-w-0 flex-1 truncate">{title}</span>
            {points.length > 0 ? (
              <span className={cn("shrink-0 tabular-nums", p.muted)}>{points.length}</span>
            ) : null}
            <ChevronDown className={cn("h-3.5 w-3.5 shrink-0", p.muted)} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80 p-1.5">
          <DropdownMenuLabel className="normal-case tracking-normal">
            <span className="block truncate text-sm font-semibold text-foreground">{title}</span>
            <span className="block text-xs font-normal">
              {points.length === 1 ? "1 point" : `${points.length} points`}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="max-h-64 overflow-y-auto px-2 py-1.5">
            {points.length > 0 ? (
              <ol className="space-y-1.5">
                {points.map((point, i) => (
                  <li
                    key={i}
                    className={cn(
                      "flex items-start gap-2.5 text-[13px] leading-5 text-foreground",
                      levels?.[i] ? "pl-6" : "",
                    )}
                  >
                    <span className="w-4 shrink-0 text-right font-mono text-xs tabular-nums text-muted-foreground">
                      {labels[i]}.
                    </span>
                    <span dir={isRTLText(point) ? "rtl" : "ltr"} className="min-w-0">
                      {point}
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-xs text-muted-foreground">
                {note.isLoading ? "Loading…" : "This note has no points yet."}
              </p>
            )}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onRemove}>
            <X className="h-4 w-4" />
            Remove from this meeting
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        type="button"
        aria-label="Remove from this meeting"
        title="Remove from this meeting (the note is kept)"
        onClick={onRemove}
        className={cn("inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full", p.chip)}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AttachList({
  tone,
  agendaNoteIds,
  ids,
  writing,
  onToggle,
  onBack,
}: {
  tone: Tone;
  agendaNoteIds: string[];
  ids: string[];
  writing: string | null;
  onToggle: (id: string) => void;
  onBack: () => void;
}) {
  const p = PALETTE[tone];
  const [search, setSearch] = useState("");
  // Notes only, never agendas (DES 12-10-2026: different things).
  const list = useNotesList({ page: 1, pageSize: 50, search, kind: "note" });
  const attached = new Set(ids);
  const agenda = new Set(agendaNoteIds);
  const items = (list.data?.items ?? []).filter((n) => n.id !== writing);

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Back"
          onClick={onBack}
          className={cn("rounded-full p-0.5", p.muted)}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="font-medium">Attach a note</span>
      </div>
      <label className={cn("flex items-center gap-2 rounded-full px-3 py-2 text-xs", p.field)}>
        <Search className="h-3.5 w-3.5 text-brand" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search your notes"
          className={cn(
            "min-w-0 flex-1 bg-transparent placeholder:text-muted-foreground focus:outline-none",
            p.text,
          )}
        />
      </label>
      <div className="max-h-[260px] overflow-y-auto pr-1">
        {list.isError && !list.data ? (
          <p className={cn("py-3 text-center text-xs", p.muted)}>
            Couldn't load your notes. Check you're signed in.
          </p>
        ) : list.isLoading ? (
          <LoaderCircle className={cn("mx-auto my-3 h-4 w-4 animate-spin", p.muted)} />
        ) : items.length === 0 ? (
          <p className={cn("py-3 text-center text-xs", p.muted)}>
            {search.trim() ? "No notes match." : "You have no notes yet."}
          </p>
        ) : (
          items.map((n) => {
            const isAgenda = agenda.has(n.id);
            const on = isAgenda || attached.has(n.id);
            return (
              <button
                key={n.id}
                type="button"
                disabled={isAgenda}
                onClick={() => onToggle(n.id)}
                className={cn(
                  "flex w-full flex-col gap-0.5 border-b px-1 py-1.5 text-left last:border-0",
                  p.line,
                )}
              >
                <span className="flex w-full items-center gap-2">
                  <span className={cn("min-w-0 flex-1 truncate text-[13px]", p.text)}>
                    {n.title?.trim() || "Untitled note"}
                  </span>
                  {on ? (
                    <CircleCheck className="h-4 w-4 shrink-0 text-brand" />
                  ) : (
                    <Circle className={cn("h-4 w-4 shrink-0", p.muted)} />
                  )}
                </span>
                <span className={cn("truncate text-[11px]", p.muted)}>
                  {formatDateTime(n.created_at)}
                  {isAgenda ? " · Agenda for this meeting" : ""}
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

/** A row carries an id so React never keys it by position (NoteComposePage).
 *  `level`: 0 = a point, 1 = a sub-point (Sub-points, DES 12-10-2026). */
type PointRow = { id: string; text: string; level: number };
let pointSeq = 0;
const makePoint = (text = "", level = 0): PointRow => {
  pointSeq += 1;
  return { id: `mp_${pointSeq}`, text, level };
};
const rowsFrom = (value: string): PointRow[] => {
  const lines = value.split("\n");
  return lines.length
    ? lines.map((l, i) =>
        l.startsWith(SUB_MARK) ? makePoint(l.slice(SUB_MARK.length), i > 0 ? 1 : 0) : makePoint(l),
      )
    : [makePoint()];
};
/** Grow the box to its content. */
function autoSize(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

// ----------------------------------------------------------------------
// Widget popup: a taller notepad the writer can resize (DES 07-10-2026:
// "notes wala section lamba hona chahiyeh, yaa khud chota bara kar lain").
// The points area grows with the points (rows are compact in the widget) up to
// a cap, then scrolls with the typed line kept in view. A grip on the card's
// top edge drags the cap taller or shorter. The Electron window follows (it
// fits its content, DESKTOP/src/widget.js `widget:height`) and grows upward
// from the pill. The cap is remembered on this computer.
// ----------------------------------------------------------------------
const WIDGET_NOTES_KEY = "vz.widgetNotesHeight";
// 380, not more, until the desktop shell with the raised panel cap ships
// (DESKTOP src/widget.js 66a3d68): installed shells cap the panel window at
// 560px, and header + title + footer take ~150px of it. Drag the grip for
// more once the new shell is out.
const WIDGET_NOTES_DEFAULT = 380;
const WIDGET_NOTES_MIN = 120;
const WIDGET_NOTES_MAX = 620;
/** The points area never starts smaller than this, empty or not. */
const WIDGET_NOTES_FLOOR = 160;

function clampNotesHeight(h: number) {
  return Math.round(Math.max(WIDGET_NOTES_MIN, Math.min(WIDGET_NOTES_MAX, h)));
}

function useWidgetNotesHeight() {
  const [height, setHeight] = useState<number>(() => {
    try {
      const v = Number(window.localStorage.getItem(WIDGET_NOTES_KEY));
      return v ? clampNotesHeight(v) : WIDGET_NOTES_DEFAULT;
    } catch {
      return WIDGET_NOTES_DEFAULT;
    }
  });
  const drag = useRef<{ y: number; h: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { y: e.screenY, h: height };
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    // Screen coordinates: the window moves under the pointer as it grows.
    // Dragging up makes it taller (the popup sits above the pill).
    setHeight(clampNotesHeight(drag.current.h + (drag.current.y - e.screenY)));
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    try {
      window.localStorage.setItem(WIDGET_NOTES_KEY, String(height));
    } catch {
      /* per-device convenience only */
    }
  };
  // The area grows with the points up to `height` (the grip sets that cap),
  // then scrolls. While dragging it shows the cap itself, so the drag is seen.
  const style: React.CSSProperties = dragging
    ? { height }
    : { minHeight: Math.min(height, WIDGET_NOTES_FLOOR), maxHeight: height };
  return { height, style, grip: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
}

/**
 * The notepad as numbered points (the Write a Note page's editor). `value`
 * is the note's text, one point per line; every edit hands the whole text
 * back. Splitting on "\n" in the change handler is the whole mechanism:
 * Return and a pasted block arrive the same way.
 */
function NumberedPoints({
  value,
  disabled,
  onChange,
  tone,
  autoFocus = false,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  /** The widget popup is always dark; the page follows the theme. */
  tone: Tone;
  autoFocus?: boolean;
}) {
  const w = tone === "widget";
  const [rows, setRows] = useState<PointRow[]>(() => rowsFrom(value));
  const emitted = useRef(value);
  const refs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const focusNext = useRef<string | null>(null);

  // Text from outside (loaded from the server, or typed in the desktop
  // widget's popup) replaces the rows; our own edits come back unchanged.
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setRows(rowsFrom(value));
  }, [value]);

  useLayoutEffect(() => {
    Object.values(refs.current).forEach(autoSize);
    const id = focusNext.current;
    if (!id) return;
    focusNext.current = null;
    const el = refs.current[id];
    el?.focus();
    el?.setSelectionRange(el.value.length, el.value.length);
    el?.scrollIntoView({ block: "nearest" });
  }, [rows]);

  const emit = (next: PointRow[]) => {
    setRows(next);
    const text = next.map((r, i) => (i > 0 && r.level > 0 ? SUB_MARK : "") + r.text).join("\n");
    emitted.current = text;
    onChange(text);
  };

  const setText = (id: string, raw: string) => {
    const idx = rows.findIndex((r) => r.id === id);
    if (idx < 0) return;
    // A point starts with a capital (DES 07-10-2026), as the phone keyboard
    // does: only the first letter typed into an empty point, so "iPhone"
    // typed over a lowercase start later is left alone.
    // A pasted tab becomes a space: a tab is this panel's sub-point mark.
    const clean = raw.replace(/\t/g, " ");
    const text = rows[idx].text === "" ? capitalizeFirst(clean) : clean;
    const lines = text.split("\n");
    if (lines.length === 1) {
      emit(rows.map((r) => (r.id === id ? { ...r, text } : r)));
      return;
    }
    // Sub-points (DES 12-10-2026): Return on a sub-point opens the next one.
    const expanded = lines.map((l, i) =>
      i === 0 ? { ...rows[idx], text: l } : makePoint(l, rows[idx].level),
    );
    focusNext.current = expanded[expanded.length - 1].id;
    emit([...rows.slice(0, idx), ...expanded, ...rows.slice(idx + 1)]);
  };

  /** Sub-points (DES 12-10-2026): one level; the first point stays a point. */
  const setLevel = (id: string, level: number) => {
    const idx = rows.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const next = idx === 0 ? 0 : Math.max(0, Math.min(1, level));
    if (rows[idx].level === next) return;
    emit(rows.map((r) => (r.id === id ? { ...r, level: next } : r)));
  };

  const remove = (id: string) => {
    if (rows.length === 1) return;
    const idx = rows.findIndex((r) => r.id === id);
    const target = rows[idx - 1] ?? rows[idx + 1];
    if (target) focusNext.current = target.id;
    delete refs.current[id];
    emit(rows.filter((r) => r.id !== id));
  };

  const labels = pointLabels(
    rows.map((r) => r.level),
    rows.length,
  );

  const addAtEnd = () => {
    const last = rows[rows.length - 1];
    if (last && !last.text.trim()) {
      focusNext.current = last.id;
      setRows([...rows]);
      return;
    }
    const row = makePoint();
    focusNext.current = row.id;
    emit([...rows, row]);
  };

  return (
    <>
      <ol>
        {rows.map((row, index) => {
          const last = index === rows.length - 1;
          // Sub-points (DES 12-10-2026): the rail runs straight past a
          // sub-point unbroken, and its letter sits indented past the rail.
          const sub = index > 0 && row.level > 0;
          const rail = w ? "bg-[#3A3938]" : "bg-border";
          return (
            <li key={row.id} className="flex gap-3">
              {sub ? (
                <div
                  className={cn("flex shrink-0 flex-col items-center", w ? "w-6" : "w-8")}
                  aria-hidden
                >
                  <span className={cn("w-0.5", w ? "h-4" : "h-5", rail)} />
                  <span className={cn("w-0.5 flex-1", last ? "bg-transparent" : rail)} />
                </div>
              ) : (
                <div
                  className={cn("flex shrink-0 flex-col items-center", w ? "w-6" : "w-8")}
                  aria-hidden
                >
                  <span
                    className={cn(
                      "w-0.5",
                      w ? "h-1" : "h-1.5",
                      index === 0 ? "bg-transparent" : w ? "bg-[#3A3938]" : "bg-border",
                    )}
                  />
                  <span
                    className={cn(
                      "font-semibold tabular-nums",
                      w ? "text-base leading-6 text-[#A3A3A3]" : "text-lg leading-7 text-muted-foreground",
                    )}
                  >
                    {labels[index]}
                  </span>
                  <span
                    className={cn(
                      "w-0.5 flex-1",
                      last ? "bg-transparent" : w ? "bg-[#3A3938]" : "bg-border",
                    )}
                  />
                </div>
              )}
              {sub ? (
                <span
                  aria-hidden
                  className={cn(
                    "ml-2 w-5 shrink-0 font-semibold leading-6",
                    w ? "py-1 text-[15px] text-[#A3A3A3]" : "py-1.5 text-base text-muted-foreground",
                  )}
                >
                  {labels[index]}.
                </span>
              ) : null}
              <textarea
                ref={(el) => {
                  refs.current[row.id] = el;
                }}
                value={row.text}
                rows={1}
                disabled={disabled}
                dir={isRTLText(row.text) ? "rtl" : "ltr"}
                onChange={(e) => {
                  autoSize(e.currentTarget);
                  // The line being typed stays in view when the points
                  // scroll (the widget popup's capped height).
                  if (w) e.currentTarget.scrollIntoView({ block: "nearest" });
                  setText(row.id, e.target.value);
                }}
                onKeyDown={(e) => {
                  // Sub-points (DES 12-10-2026): Tab indents, Shift+Tab
                  // outdents, focus stays in the row.
                  if (e.key === "Tab") {
                    e.preventDefault();
                    setLevel(row.id, e.shiftKey ? 0 : 1);
                    return;
                  }
                  if (e.key === "Backspace" && row.text.length === 0 && sub) {
                    e.preventDefault();
                    setLevel(row.id, 0);
                    return;
                  }
                  if (e.key === "Backspace" && row.text.length === 0 && rows.length > 1) {
                    e.preventDefault();
                    remove(row.id);
                  }
                }}
                placeholder={
                  disabled
                    ? "Loading…"
                    : index === 0
                      ? "First point…"
                      : sub
                        ? "Sub-point…"
                        : "Next point…"
                }
                autoFocus={autoFocus && index === rows.length - 1}
                className={cn(
                  "w-full resize-none overflow-hidden border-0 bg-transparent px-0 leading-6 outline-none focus:ring-0",
                  // The widget's rows are compact (DES 07-10-2026: "user can
                  // see max notes"): 34px a point instead of 52px.
                  w
                    ? "mb-0.5 min-h-8 py-1 text-[15px] text-[#f2f2f2] placeholder:text-[#A3A3A3]/60"
                    : "mb-3 min-h-10 py-1.5 text-base text-foreground placeholder:text-muted-foreground/50",
                )}
              />
            </li>
          );
        })}
      </ol>
      {/* Clicking under the last point opens the next, as on the page. */}
      <button
        type="button"
        onClick={addAtEnd}
        aria-label="Add a point"
        className={cn("w-full cursor-text", w ? "min-h-[48px]" : "min-h-[96px]")}
      />
    </>
  );
}

export function MeetingNotesPanel({
  tone,
  meetingTitle,
  agendaNoteIds,
  meetingNoteIds,
  writingNoteId,
  onChange,
  onClose,
  getElapsedSecs,
  heading = "Your notes",
  allowAttach = true,
  titleField,
}: MeetingNotesPanelProps) {
  const p = PALETTE[tone];
  const [mode, setMode] = useState<"write" | "attach">("write");
  // What these notes do (DES 11-10-2026: "is screen per hint hona chahiyeh"):
  // the hold-ring icon by the heading + its bubble, opening on its own until
  // closed once. Web recorder only; the widget's popup is too small for it.
  const showHint = tone === "page";
  const tips = useHintPopover(titleField ? "recorder_take_notes" : "recorder_your_notes");
  const [meetingTitleDraft, setMeetingTitleDraft] = useState(titleField?.initial ?? "");
  const pointsRef = useRef<HTMLDivElement | null>(null);

  // Local mirror of the recorder's ids (see @details).
  const [ids, setIds] = useState(meetingNoteIds);
  const [writing, setWriting] = useState<string | null>(writingNoteId);
  const idsKey = meetingNoteIds.join(",");
  useEffect(() => {
    setIds(meetingNoteIds);
    setWriting(writingNoteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, writingNoteId]);
  const commit = (nextIds: string[], nextWriting: string | null) => {
    setIds(nextIds);
    setWriting(nextWriting);
    onChange(nextIds, nextWriting);
  };

  const existing = useNote(writingNoteId);
  const create = useCreateTypedNote();
  const update = useUpdateNote();

  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(!writingNoteId);
  const [saved, setSaved] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const idRef = useRef<string | null>(writingNoteId);
  const creating = useRef(false);
  const pending = useRef<number | null>(null);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  useEffect(() => {
    if (writingNoteId && !idRef.current) {
      idRef.current = writingNoteId;
      // The note arrived after the panel opened (the recorder restores it
      // from storage a moment later, e.g. after a refresh): load its points
      // instead of starting blank, so the next save cannot overwrite them
      // with only the new lines (DES 11-10-2026). Not when the person has
      // already typed; a note this panel created sets idRef itself.
      if (!text) setLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writingNoteId]);

  // ----------------------------------------------------------------------
  // Line timing (ADR-030, 06-10-2026)
  // line text -> the second it first read that way; null = typed before this
  // panel had a clock for it (a line loaded from an older save). Seeded from
  // the note's stored offsets when the panel reopens, so a reopened popup
  // keeps the seconds it already sent.
  // Related: BoardroomRecorderPage.tsx, DesktopWidgetPage.tsx (the clocks)
  // ----------------------------------------------------------------------
  const clockRef = useRef(getElapsedSecs);
  clockRef.current = getElapsedSecs;
  const stampsRef = useRef(new Map<string, number | null>());
  const stampLines = (value: string) => {
    const clock = clockRef.current;
    if (!clock) return;
    const now = Math.max(0, Math.floor(clock()));
    for (const line of noteLines(value)) {
      if (!stampsRef.current.has(line)) stampsRef.current.set(line, now);
    }
  };
  const offsetsFor = (value: string): Array<number | null> | undefined => {
    if (!clockRef.current) return undefined;
    stampLines(value);
    return noteLines(value).map((l) => stampsRef.current.get(l) ?? null);
  };

  // Reopening picks the note back up from the server.
  useEffect(() => {
    if (loaded || !existing.data) return;
    const lines = notePoints(existing.data);
    const offsets = storedOffsets(existing.data, lines);
    lines.forEach((line, i) => {
      if (!stampsRef.current.has(line)) stampsRef.current.set(line, offsets?.[i] ?? null);
    });
    setText(pointsText(lines, notePointLevels(existing.data, lines)));
    setLoaded(true);
  }, [existing.data, loaded]);

  const save = async (value: string) => {
    if (!value.trim()) return;
    setSaved("saving");
    const pointOffsets = offsetsFor(value);
    // Sub-points (DES 12-10-2026): clean lines out (no tab marks) and their
    // levels beside them.
    const lines = noteLines(value);
    const pointLevels = noteLevels(value);
    const body = lines.join("\n");
    try {
      if (idRef.current) {
        // The lines always go as `points` too: the server stores
        // `pointOffsets` and `pointLevels` only alongside `points`. `text`
        // stays, because shipped clients still read a typed note's body from it.
        await update.mutateAsync({
          id: idRef.current,
          text: body,
          points: lines,
          pointLevels,
          ...(pointOffsets ? { pointOffsets } : {}),
        });
      } else if (!creating.current) {
        creating.current = true;
        const firstLine =
          value
            .split("\n")
            .find((l) => l.trim())
            ?.trim() ?? "";
        const note = await create.mutateAsync({
          title: (meetingTitle ? `Notes: ${meetingTitle}` : firstLine).slice(0, 120),
          text: body,
          pointOffsets,
          pointLevels,
        });
        idRef.current = note.id;
        creating.current = false;
        commit([note.id, ...idsRef.current.filter((x) => x !== note.id)], note.id);
      }
      setSaved("saved");
    } catch {
      creating.current = false;
      setSaved("error");
    }
  };

  // The same note can be open in the desktop widget's popup AND on the page.
  // Coming back to a window picks up words typed in the other one, unless
  // this one has unsaved typing of its own.
  const refetchNote = existing.refetch;
  useEffect(() => {
    const onFocus = () => {
      if (!idRef.current || pending.current || creating.current) return;
      void refetchNote().then((r) => {
        if (r.data && !pending.current) {
          const lines = notePoints(r.data);
          setText(pointsText(lines, notePointLevels(r.data, lines)));
        }
      });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refetchNote]);

  const onType = (value: string) => {
    // Stamped as typed, not at save time: the save waits SAVE_DELAY_MS.
    stampLines(value);
    setText(value);
    if (pending.current) window.clearTimeout(pending.current);
    pending.current = window.setTimeout(() => {
      pending.current = null;
      void save(value);
    }, SAVE_DELAY_MS);
  };

  // Closing mid-sentence still saves the last words.
  const latestText = useRef(text);
  latestText.current = text;
  useEffect(
    () => () => {
      if (pending.current) {
        window.clearTimeout(pending.current);
        void save(latestText.current);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const attached = ids.filter((id) => id !== writing);
  const notesSize = useWidgetNotesHeight();
  const toggle = (id: string) =>
    commit(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id], writing);

  return (
    <div className={cn("relative flex flex-col gap-2 text-[13px]", p.surface)}>
      {tone === "widget" && mode !== "attach" ? (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Drag to resize the notes"
          title="Drag to resize"
          {...notesSize.grip}
          className="absolute inset-x-0 top-0 z-10 flex h-3 cursor-ns-resize touch-none justify-center pt-1"
        >
          <span className="h-1 w-9 rounded-full bg-white/20" />
        </div>
      ) : null}
      {mode === "attach" ? (
        <AttachList
          tone={tone}
          agendaNoteIds={agendaNoteIds}
          ids={ids}
          writing={writing}
          onToggle={toggle}
          onBack={() => setMode("write")}
        />
      ) : (
        <>
          <Header
            tone={tone}
            title={heading}
            titleAdornment={
              showHint ? (
                <HintIcon open={tips.open} onClick={tips.toggle} anchorRef={tips.anchorRef} />
              ) : null
            }
            onClose={onClose}
            right={
              saved === "saving" ? (
                <span className={cn("text-xs", p.muted)}>Saving…</span>
              ) : saved === "saved" ? (
                <span className={cn("inline-flex items-center gap-1 text-xs", p.muted)}>
                  <Check className="h-3 w-3" />
                  Saved
                </span>
              ) : saved === "error" ? (
                <span className="text-xs text-brand">Not saved, keep typing to retry</span>
              ) : null
            }
          />
          {showHint && tips.open ? (
            <HintBubble
              lines={
                titleField
                  ? [
                      "Type a point and press Return for the next. Each one is stamped with the meeting time.",
                      // Sub-points (DES 12-10-2026).
                      "Tab makes a point a sub-point of the one above; Shift+Tab makes it a point again.",
                      "Your points lead the meeting notes, marked \u201cYour note\u201d, with what was said under them.",
                      "A point the meeting did not clearly cover is kept under \u201cAlso in your notes\u201d.",
                    ]
                  : [
                      "Type a point and press Return for the next. Each one is stamped with the meeting time.",
                      // Sub-points (DES 12-10-2026).
                      "Tab makes a point a sub-point of the one above; Shift+Tab makes it a point again.",
                      "Only you see these. When the meeting ends, each note gets a report of what was said around its points.",
                      "They never change the shared meeting summary.",
                    ]
              }
              onClose={tips.close}
              anchorRef={tips.anchorRef}
              className="mt-3"
            />
          ) : null}
          {titleField ? (
            // The Write a Note page's title: the first line, no box. A
            // textarea so a long title wraps (DES 07-10-2026); Return goes to
            // the points instead of breaking the title. The widget's popup
            // has the same look, smaller (DES 07-10-2026).
            <textarea
              ref={autoSize}
              rows={1}
              value={meetingTitleDraft}
              onChange={(e) => {
                const typed = e.target.value.replace(/\s*\n\s*/g, " ");
                const v = meetingTitleDraft === "" ? capitalizeFirst(typed) : typed;
                autoSize(e.currentTarget);
                setMeetingTitleDraft(v);
                titleField.onChange(v);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  pointsRef.current?.querySelector("textarea")?.focus();
                }
              }}
              maxLength={100}
              // First letter a capital when left (DES 12-10-2026).
              autoCapitalize="sentences"
              onBlur={() => {
                const v = capitalizeFirst(meetingTitleDraft);
                if (v !== meetingTitleDraft) {
                  setMeetingTitleDraft(v);
                  titleField.onChange(v);
                }
              }}
              aria-label="Meeting title"
              placeholder="Meeting title (optional)"
              dir={isRTLText(meetingTitleDraft) ? "rtl" : "ltr"}
              className={cn(
                "mt-2 w-full resize-none overflow-hidden border-0 bg-transparent p-0 font-bold outline-none focus:ring-0",
                tone === "widget"
                  ? "text-[20px] leading-[26px] text-[#f2f2f2] placeholder:text-[#A3A3A3]/60"
                  : "text-[26px] leading-[34px] text-foreground placeholder:text-muted-foreground/50",
              )}
            />
          ) : null}
          <div
            ref={pointsRef}
            className={cn(
              titleField ? "mt-3" : "mt-1",
              // The popup is a card over other apps: its points scroll in a
              // height the writer sets with the grip (useWidgetNotesHeight).
              tone === "widget" && "overflow-y-auto pr-1",
            )}
            style={tone === "widget" ? notesSize.style : undefined}
          >
            <NumberedPoints
              tone={tone}
              autoFocus={tone === "widget" && !titleField}
              value={text}
              disabled={!loaded}
              onChange={onType}
            />
          </div>
          {attached.length > 0 ? (
            <>
              <span className={cn("text-[11px]", p.muted)}>Attached ({attached.length})</span>
              <div className="max-h-[110px] space-y-1.5 overflow-y-auto pr-1">
                {attached.map((id) => (
                  <AttachedRow
                    key={id}
                    tone={tone}
                    id={id}
                    onRemove={() =>
                      commit(
                        ids.filter((x) => x !== id),
                        writing,
                      )
                    }
                  />
                ))}
              </div>
            </>
          ) : null}
          {allowAttach ? (
            <button
              type="button"
              onClick={() => setMode("attach")}
              className={cn(
                "flex items-center gap-2 rounded-full py-1.5 pl-3 pr-2 text-xs",
                p.chip,
              )}
            >
              <Paperclip className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">Attach an existing note</span>
            </button>
          ) : null}
          <span className={cn("inline-flex items-center gap-1.5 text-xs", p.muted)}>
            <Lock className="h-3 w-3" />
            Only you see these · linked when the meeting ends
          </span>
        </>
      )}
    </div>
  );
}
